import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OpportunityStatus } from 'backend/types'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { updateOpportunityStatus } from '@/lib'
import { updateOpportunityListCache } from '@/lib/queryClient/optimisticUpdate'
import type { OpportunityListItem } from './useOpportunities'

export interface UseOpportunityStatusChangeOptions {
  /** ステータス一覧 */
  opportunityStatuses: OpportunityStatus[]
  /** クローズ理由ダイアログを開くコールバック */
  onOpenCloseReasonDialog: (opportunity: { id: number; title: string }, statusId: number) => void
  /** G会社商流ダイアログを開くコールバック */
  onOpenGCompanyDialog: (
    opportunity: { id: number; title: string; g_company_name?: string | null; g_company_contact?: string | null },
    statusId: number,
  ) => void
}

/**
 * オポチュニティのステータス変更を管理するカスタムフック
 *
 * 通常のステータス変更に加え、Close処理やG会社商流など
 * 追加情報が必要なステータス変更に対応します。
 */
export function useOpportunityStatusChange(options: UseOpportunityStatusChangeOptions) {
  const { opportunityStatuses, onOpenCloseReasonDialog, onOpenGCompanyDialog } = options

  const queryClient = useQueryClient()

  // ステータス更新ミューテーション
  const updateStatusMutation = useMutation({
    mutationFn: async (params: {
      opportunityId: number
      opportunityStatusId: number
      opportunityCloseReason?: {
        opportunityCloseReasonId: number
        opportunityAdditionalCloseReason?: string | null
      }
      gCompanyName?: string | null
      gCompanyContact?: string | null
    }) => {
      return updateOpportunityStatus(params.opportunityId, {
        opportunityStatusId: params.opportunityStatusId,
        opportunityCloseReason: params.opportunityCloseReason,
        gCompanyName: params.gCompanyName,
        gCompanyContact: params.gCompanyContact,
      })
    },
    onSuccess: (updatedStatus, variables) => {
      // 成功したら一覧のキャッシュを更新（全タブのキャッシュを更新）
      // 更新されたステータス名を取得
      const statusName =
        opportunityStatuses.find((s) => s.id === variables.opportunityStatusId)?.name ||
        updatedStatus.opportunity_status_name

      updateOpportunityListCache(queryClient, variables.opportunityId, () => ({
        opportunity_status_id: updatedStatus.opportunity_status_id,
        opportunity_status_name: statusName,
        opportunity_close_reason_id: updatedStatus.opportunity_close_reason_id,
        opportunity_close_reason: updatedStatus.opportunity_close_reason,
        opportunity_additional_close_reason: updatedStatus.opportunity_additional_close_reason,
        g_company_name: updatedStatus.g_company_name,
        g_company_contact: updatedStatus.g_company_contact,
        updated_at: updatedStatus.updated_at,
      }))
    },
    onError: () => {
      toast.error('ステータスの更新に失敗しました')
    },
  })

  // ステータス変更ハンドラー
  const handleStatusChangeClick = useCallback(
    (opportunity: OpportunityListItem, statusId: number) => {
      const selectedStatus = opportunityStatuses.find((s) => s.id === statusId)

      if (!selectedStatus) return

      // Close処理（ステータスID=2、またはis_completed=true）
      if (selectedStatus.is_completed) {
        onOpenCloseReasonDialog(
          {
            id: opportunity.id,
            title: opportunity.title,
          },
          statusId,
        )
        return
      }

      // G会社商流処理（ステータスID=7）
      if (statusId === 7) {
        onOpenGCompanyDialog(
          {
            id: opportunity.id,
            title: opportunity.title,
            g_company_name: opportunity.g_company_name,
            g_company_contact: opportunity.g_company_contact,
          },
          statusId,
        )
        return
      }

      // 通常のステータス変更
      updateStatusMutation.mutate({
        opportunityId: opportunity.id,
        opportunityStatusId: statusId,
      })
    },
    [opportunityStatuses, updateStatusMutation, onOpenCloseReasonDialog, onOpenGCompanyDialog],
  )

  // クローズ理由送信ハンドラー
  const handleCloseReasonSubmit = useCallback(
    (
      opportunityId: number,
      statusId: number,
      data: {
        opportunityCloseReasonId: number
        opportunityAdditionalCloseReason?: string | null
      },
    ) => {
      updateStatusMutation.mutate({
        opportunityId,
        opportunityStatusId: statusId,
        opportunityCloseReason: data,
      })
    },
    [updateStatusMutation],
  )

  // G会社商流送信ハンドラー
  const handleGCompanySubmit = useCallback(
    (opportunityId: number, statusId: number, data: { gCompanyName: string; gCompanyContact: string }) => {
      updateStatusMutation.mutate({
        opportunityId,
        opportunityStatusId: statusId,
        gCompanyName: data.gCompanyName,
        gCompanyContact: data.gCompanyContact,
      })
    },
    [updateStatusMutation],
  )

  return {
    updateStatusMutation,
    handleStatusChangeClick,
    handleCloseReasonSubmit,
    handleGCompanySubmit,
  }
}
